import React, { useState, useEffect, useContext, useMemo } from 'react'
import ReactBSAlert from 'react-bootstrap-sweetalert'
import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import BrokerAPI from 'config/api/BrokerAPI'
import FormGroup from '@material-ui/core/FormGroup'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown'
import Button from '@material-ui/core/Button'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import RefreshIcon from '@material-ui/icons/Refresh'
import { onError } from 'config/lib/errorLib'
import Loader from 'components/general/Loader'
import AllocationsTable from 'components/broker/AllocationsTable'
import BrokersCard from 'components/broker/BrokersCard'
import AllocationStats from 'components/broker/AllocationStats'
import { defaultProducts } from 'config/constants/Constants'
import { getProductString } from 'config/helpers/Helpers'

import DetailsContext from 'config/context/DetailsContext'

function Allocations() {
    const api = new BrokerAPI()
    const companyDetails = useContext(DetailsContext)
    const companyId = companyDetails.companyId

    const nextFridayDate = useMemo(() => {
        const now = new Date()
        const day = now.getDay() // 0=Sun, 5=Fri
        let daysUntilFriday = 5 - day
        if (daysUntilFriday < 0) daysUntilFriday += 7
        const nextFri = new Date(now)
        nextFri.setDate(now.getDate() + daysUntilFriday)
        return nextFri.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    }, [])
    const productsAvailable = companyDetails.productsAvailable || defaultProducts
    let initialProduct = 'income-protection'
    if (productsAvailable['income-protection'] === false || productsAvailable['income-protection'] === undefined) {
        for (let key of Object.keys(productsAvailable)) {
            if (productsAvailable[key] === true) {
                console.log(key)
                initialProduct = key
            }
        }
    }
    console.log('products available: ', productsAvailable)
    console.log('initial product: ', initialProduct)

    const [details, setDetails] = useState(null)
    const [companyData, setCompanyData] = useState(companyDetails)
    const [allBrokers, setAllBrokers] = useState([])
    const [brokers, setBrokers] = useState([])
    const [invites, setInvites] = useState([])
    //const [broker, setBroker] = useState(null)
    const [product, setProduct] = useState(initialProduct)
    const [allocated, setAllocated] = useState([])
    const [delivery, setDelivery] = useState(null)
    const [loading, setLoading] = useState(true)
    const [alert, setAlert] = useState(false)
    const [weekView, setWeekView] = useState('this-week')
    const [liveNextAllocations, setLiveNextAllocations] = useState(null)
    const [refreshing, setRefreshing] = useState(false)

    //const weeklyLimit = parseInt(companyDetails.weeklyLimits[product]) + parseInt(companyDetails.extraWeeklyLimits[product])

    useEffect(() => {
        getDetails()
    }, [])

    async function getDetails() {
        try {
            const details = await api.getDetails(companyId)
            console.log('details: ', details)

            const company = await api.getCompanyDetails(companyId)
            console.log('company: ', company)

            let brokers = await api.getBrokers(companyId)
            console.log('brokers: ', brokers)

            let activeBrokers = brokers.filter(broker => (!broker.hideInAnalytics) && (!broker.products || broker.products[0] === 'All' || broker.products.indexOf(product) > -1))

            const invites = await api.getInvites(companyId)
            console.log('invites: ', invites)

            const allocated = await api.getAllocatedLeads(companyId, product)
            console.log('allocated: ', allocated)

            const delivery = await api.getDelivery(companyId, product)
            console.log('delivery: ', delivery)

            setDetails(details)
            setCompanyData(company)
            setAllBrokers(brokers)
            setBrokers(activeBrokers)
            setInvites(invites)
            setAllocated(allocated)
            setDelivery(delivery)
            setLoading(false)
            setRefreshing(false)
        } catch(e) {
            onError(e)
            setRefreshing(false)
        }
    }

    async function refreshData() {
        setRefreshing(true)
        await getDetails()
        showSuccessAlert('Allocations refreshed successfully')
    }

    function updateBroker(broker) {
        setLoading(true)

        // Update the product-filtered list used by the allocations table
        setBrokers(prev => {
            const index = prev.findIndex(oldBroker => (oldBroker.brokerId === broker.brokerId))
            if (index === -1) return prev
            const newBrokers = [...prev]
            newBrokers[index] = broker
            return newBrokers
        })

        // Update the full list used by the Brokers sidebar (independent of product)
        setAllBrokers(prev => {
            const index = prev.findIndex(oldBroker => (oldBroker.brokerId === broker.brokerId))
            if (index === -1) return [...prev, broker]
            const newBrokers = [...prev]
            newBrokers[index] = broker
            return newBrokers
        })

        setLoading(false)
    }

    async function updateProduct(product) {
        setProduct(product)
        setLoading(true)
        try {
            const allocated = await api.getAllocatedLeads(companyId, product)
            console.log('allocated: ', allocated)

            const delivery = await api.getDelivery(companyId, product)
            console.log('delivery: ', delivery)

            let activeBrokers = allBrokers.filter(broker => (!broker.hideInAnalytics) && (!broker.products || broker.products[0] === 'All' || broker.products.indexOf(product) > -1))
            console.log('all brokers: ', allBrokers)
            console.log('active brokers: ', activeBrokers)

            setAllocated(allocated)
            setDelivery(delivery)
            setBrokers(activeBrokers)
            setLoading(false)
        } catch(e) {
            onError(e)
        }
    }

    function getCurrentAllocation() {
        if (!delivery) return 0
        if (weekView === 'next-week') {
            // If there are unsaved live edits in the table, show that total
            if (liveNextAllocations) {
                return Object.values(liveNextAllocations).reduce((sum, v) => sum + (parseInt(v) || 0), 0)
            }
            // Derive from company nextAllocations (single source of truth)
            if (companyData.nextAllocations && companyData.nextAllocations[product]) {
                const splits = companyData.nextAllocations[product]
                const total = Object.values(splits).reduce((sum, v) => sum + parseInt(v || 0), 0)
                return total
            }
            // Fallback to delivery.nextAllocation (legacy) or current allocation
            return delivery.nextAllocation !== undefined && delivery.nextAllocation !== null 
                ? parseInt(delivery.nextAllocation) 
                : (parseInt(delivery.allocation) || 0)
        }
        return parseInt(delivery.allocation) || 0
    }

    function showSuccessAlert(text) {
        setAlert(
            <ReactBSAlert
                success
                style={{ display: 'block', marginTop: '-100px' }}
                title='Success'
                onConfirm={() => setAlert(false)}
                onCancel={() => setAlert(false)}
                confirmBtnBsStyle='success'
                confirmBtnText='Ok'
                btnSize=''
                confirmBtnStyle={{
                    marginRight: undefined,
                    borderColor: undefined,
                    boxShadow: undefined,
                }}
            >
                {text}
            </ReactBSAlert>
        )
    }

    function onDeliveryUpdated(updatedDelivery) {
        setDelivery(updatedDelivery)
    }

    return (
        <>
            <Container
                maxWidth={false}
                component={Box}
                marginTop='2rem'
                //classes={{ root: classes.containerRoot }}
            >
                {alert}
                {!loading ? (
                    <Grid container justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Grid
                                container
                                component={Box}
                                alignItems='center'
                                justifyContent='flex-start'
                                style={{marginBottom: '2rem'}}
                                direction='row'
                                spacing={2}
                            >
                                <Grid item xs={12} md={2}>
                                    <FormGroup style={{marginBottom: '0'}}>
                                        <FormControl variant='outlined' fullWidth>
                                            <Select
                                                multiple={false}
                                                IconComponent={KeyboardArrowDown}
                                                value={product}
                                                onChange={(e) => updateProduct(e.target.value)}
                                            >
                                                {Object.keys(productsAvailable).map((product) => {
                                                    if (productsAvailable[product]) {
                                                        return (<MenuItem value={product}>{getProductString(product)}</MenuItem>)
                                                    }
                                                })}
                                            </Select>
                                        </FormControl>
                                    </FormGroup>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <ButtonGroup size='small'>
                                        <Button
                                            onClick={() => setWeekView('this-week')}
                                            style={{
                                                backgroundColor: weekView === 'this-week' ? '#5e72e4' : 'transparent',
                                                color: weekView === 'this-week' ? '#fff' : '#5e72e4',
                                                border: '1px solid #5e72e4',
                                                textTransform: 'none',
                                                padding: '6px 16px'
                                            }}
                                        >
                                            This Week
                                        </Button>
                                        <Button
                                            onClick={() => setWeekView('next-week')}
                                            style={{
                                                backgroundColor: weekView === 'next-week' ? '#5e72e4' : 'transparent',
                                                color: weekView === 'next-week' ? '#fff' : '#5e72e4',
                                                border: '1px solid #5e72e4',
                                                textTransform: 'none',
                                                padding: '6px 16px'
                                            }}
                                        >
                                            Next Week
                                        </Button>
                                    </ButtonGroup>
                                </Grid>
                                <Grid item style={{marginLeft: 'auto'}}>
                                    <Button
                                        onClick={refreshData}
                                        disabled={refreshing}
                                        size='small'
                                        style={{
                                            color: '#5e72e4',
                                            border: '1px solid #5e72e4',
                                            textTransform: 'none',
                                            padding: '6px 16px',
                                            minWidth: 'auto'
                                        }}
                                        startIcon={<RefreshIcon style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />}
                                    >
                                        {refreshing ? 'Refreshing...' : 'Refresh'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Box
                                display='flex'
                                alignItems='center'
                                style={{
                                    backgroundColor: '#f6f9fc',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '6px',
                                    padding: '12px 16px',
                                    marginBottom: '1.5rem'
                                }}
                            >
                                <InfoOutlined style={{ color: '#5e72e4', marginRight: '10px', fontSize: '1.3rem' }} />
                                <Box component='span' fontSize='.875rem' fontWeight='500' color='#525f7f'>
                                    Changes to next week's allocations will take effect on the evening of <strong>{nextFridayDate}</strong>. This week's allocations can not be changed.
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <Grid container direction='row' spacing={1}>
                                <Grid item xs={12}>
                                    <AllocationStats 
                                        allocation={getCurrentAllocation()}
                                        extraAllocation={delivery ? (parseInt(delivery.extraAllocation) || 0) : 0}
                                        delivered={weekView === 'this-week' ? allocated.total : 0}
                                        weekView={weekView}
                                        currentAllocation={delivery ? (parseInt(delivery.allocation) || 0) : 0}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AllocationsTable
                                        brokers={brokers}
                                        product={product}
                                        companyDetails={companyData}
                                        allocated={weekView === 'this-week' ? allocated.allocated : []}
                                        weeklyLimit={getCurrentAllocation()}
                                        weekView={weekView}
                                        delivery={delivery}
                                        onDeliveryUpdated={onDeliveryUpdated}
                                        onLiveAllocationsChange={setLiveNextAllocations}
                                        details={details}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <BrokersCard
                                brokers={allBrokers}
                                invites={invites}
                                refreshBrokers={() => getDetails()}
                                updateBroker={(broker) => updateBroker(broker)}
                                showSuccessAlert={(text) => showSuccessAlert(text)}
                                allocations={companyDetails.allocations}
                                product={product}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='center' alignItems='center' style={{height: '20vh'}}>
                                <Loader />
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </>
    )
}

export default Allocations